'use client';

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';

import { SkeletonRoute } from './SkeletonRoute';
import { skeletonForPath } from './route-skeleton-shapes';

/**
 * Client-side skeleton-first navigation.
 *
 * `loading.tsx` is a SERVER boundary. Next renders it only once it has begun
 * fetching the target route's payload, so the skeleton is a consequence of the
 * request, not of the click. Measured against a 1.5s-delayed payload: the
 * skeleton appeared 3,403ms after the click, and with the network offline it
 * never appeared at all. A click that changes nothing is indistinguishable
 * from a click that was not received, which is why users click repeatedly.
 *
 * The target route is known synchronously at click time, so its skeleton can
 * be rendered on that same tick from a static map — no network, no server, no
 * await. The server boundary is untouched and still covers the slow-data case;
 * this covers the window before the server can respond at all.
 *
 * The skeleton clears when `usePathname()` reports the new route, which is the
 * point React has the real page to show.
 */
interface NavigationSkeletonContextValue {
  /** Show the target route's skeleton immediately. Called from the click. */
  beginNavigation: (href: string) => void;
  /** The route whose skeleton is currently showing, if any. */
  pendingHref: string | null;
}

const NavigationSkeletonContext = createContext<NavigationSkeletonContextValue | null>(null);

/**
 * Access the skeleton-first navigation hook.
 *
 * Returns a no-op outside the provider so a component rendered in isolation
 * (a test, a storybook entry) does not crash — navigation there simply has no
 * skeleton to drive.
 */
export function useNavigationSkeleton(): NavigationSkeletonContextValue {
  const ctx = useContext(NavigationSkeletonContext);
  if (!ctx) {
    // Deliberately throws rather than returning a silent no-op.
    //
    // The first wiring of this put the provider around `<main>` only, while
    // the Sidebar that calls `beginNavigation` was its SIBLING. A no-op
    // fallback made that compile, typecheck, render and navigate correctly
    // while doing nothing at all — the skeleton simply never appeared, with
    // no error anywhere. A throw turns that into an immediate, locatable
    // failure instead of a feature that is quietly absent.
    throw new Error(
      'useNavigationSkeleton must be used inside <NavigationSkeletonProvider>. ' +
        'The provider has to wrap every component that triggers navigation, ' +
        'not only the element the page renders into.',
    );
  }
  return ctx;
}

/**
 * Which route's skeleton should be on screen, given the route we are waiting
 * for and where the router currently is.
 *
 * Extracted and exported so the lifecycle is testable. The suite runs in
 * vitest's `node` environment with no DOM and no testing-library, so a rendered
 * provider cannot be asserted against — which is why the sibling tests in this
 * directory inspect source text instead. A pure function is the part that
 * actually encodes the rule, so it is the part worth testing directly.
 *
 * On a FAILED navigation the pathname never becomes `pendingHref`, so this keeps
 * returning it and the skeleton stays up. That is pre-existing behaviour: the
 * effect this replaced also only cleared on `pathname === pendingHref`, so it
 * never fired in that case either. The comment it carried claimed the opposite
 * and was simply wrong.
 */
export function activeSkeletonHref(
  pendingHref: string | null,
  pathname: string,
): string | null {
  // Not navigating.
  if (pendingHref === null) return null;
  // Arrived: React has the real page, so showing its skeleton would be a lie.
  if (pendingHref === pathname) return null;
  return pendingHref;
}

export function NavigationSkeletonProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const beginNavigation = useCallback((href: string) => {
    // Ignore a click on the route we are already showing: replacing a rendered
    // page with its own skeleton would be a visible regression, not feedback.
    if (href === pathname) return;
    // Only cover routes we have a shape for. Without one the honest thing is
    // to leave the current page up rather than flash a guessed layout.
    if (!skeletonForPath(href)) return;
    setPendingHref(href);
  }, [pathname]);

  // DERIVED, not cleared in an effect. A skeleton is showing only while we are
  // waiting for a route that is not the one already rendered, so "has the
  // navigation finished?" is answerable during render by comparing the two —
  // there is no state to synchronise.
  //
  // This previously ran as `useEffect(() => { if (pathname === pendingHref)
  // setPendingHref(null) })`, which `react-hooks/set-state-in-effect` fails as
  // an error (it schedules a second render pass purely to discard a value the
  // first pass could already see). Deriving is both cheaper and simpler.
  //
  // `pendingHref` state is deliberately left holding the arrived route rather
  // than being reset. Nothing reads it directly — consumers get `activeHref`
  // through the context — and the next `beginNavigation` overwrites it.
  const activeHref = activeSkeletonHref(pendingHref, pathname);

  const value = useMemo(
    () => ({ beginNavigation, pendingHref: activeHref }),
    [beginNavigation, activeHref],
  );

  // The provider wraps the WHOLE shell so the sidebar can reach it, and
  // renders `children` untouched. `NavigationSkeletonSlot` below decides what
  // the page area shows — which keeps chrome (sidebar, topbar) on screen while
  // only the content region is replaced, exactly as the server boundary does.
  return (
    <NavigationSkeletonContext.Provider value={value}>
      {children}
    </NavigationSkeletonContext.Provider>
  );
}

/**
 * The page-content area: the target route's skeleton while navigating, the
 * real content otherwise.
 *
 * Separate from the provider because the provider must sit ABOVE the sidebar
 * (so clicks can reach it) while the swap must happen BELOW it (so the sidebar
 * stays on screen and does not itself get replaced by a skeleton).
 */
export function NavigationSkeletonSlot({ children }: { children: React.ReactNode }) {
  const { pendingHref } = useNavigationSkeleton();
  const spec = pendingHref ? skeletonForPath(pendingHref) : null;

  if (!spec) return <>{children}</>;

  return (
    <div data-navigation-skeleton={pendingHref}>
      <SkeletonRoute
        shape={spec.shape}
        stats={spec.stats}
        rows={spec.rows}
        cols={spec.cols}
      />
    </div>
  );
}
